import React, { useState, useEffect } from "react";
import InfoWithTooltip from "./InfoWithTooltip";
import GoogleStatusBlock from "./GoogleStatusBlock";

const microsToDollars = (micros) =>
  micros == null ? "" : (Number(micros) / 1_000_000).toFixed(2);
const dollarsToMicros = (dollars) =>
  dollars === "" || dollars == null ? null : Math.round(parseFloat(dollars) * 1_000_000);

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";

// Map Overview field labels to ai_explanations keys from Stage 3b
const EXPLANATION_KEYS = {
  "Campaign name": "campaign.name",
  "Budget name": "campaign.budget_name",
  "Budget amount (USD)": "campaign.amount_micros",
  "Delivery method": "campaign.delivery_method",
  "Advertising channel type": "campaign.advertising_channel_type",
  "Contains EU political advertising": "campaign.contains_eu_political_advertising",
  "Start date": "campaign.start_date_time",
  "End date": "campaign.end_date_time",
};

const EXPLANATION_KEY_ALIASES = {
  "Start date": ["campaign.start_date_time", "campaign.start_date"],
  "End date": ["campaign.end_date_time", "campaign.end_date"],
};

// Date only (YYYY-MM-DD) for start/end; Google requires start 00:00:00 and end 23:59:59 on publish.
const toDateValue = (v) => {
  if (v == null || v === "") return "";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return "";
  }
};

// Format date for display (read-only)
const formatDateDisplay = (v) => {
  if (v == null || v === "") return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { dateStyle: "short" });
  } catch {
    return "—";
  }
};

const GoogleCampaignOverviewPanel = ({ campaign, onSave, onRefetch, toast }) => {
  const [name, setName] = useState("");
  const [budgetName, setBudgetName] = useState("");
  const [amountDollars, setAmountDollars] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingStatus, setSyncingStatus] = useState(false);

  const explanations = campaign?.ai_explanations || {};

  const getExplanation = (label) => {
    const aliases = EXPLANATION_KEY_ALIASES[label];
    if (aliases) {
      const text = aliases.map((key) => explanations[key]).find((t) => t && t.trim());
      return text ? text.trim() : null;
    }
    const key = EXPLANATION_KEYS[label];
    const fromAi = key ? explanations[key] : null;
    return fromAi && fromAi.trim() ? fromAi.trim() : null;
  };

  const FieldLabel = ({ label }) => {
    const text = getExplanation(label);
    return (
      <span className="ga-overview-panel__field-label-wrap">
        <span className="ga-overview-panel__field-label">{label}</span>
        {text && <InfoWithTooltip text={text} />}
      </span>
    );
  };

  useEffect(() => {
    if (campaign) {
      setName(campaign.name || "");
      setBudgetName(campaign.budget_name || "");
      setAmountDollars(microsToDollars(campaign.amount_micros));
      setStartDate(campaign.start_date || toDateValue(campaign.start_date_time));
      setEndDate(campaign.end_date || toDateValue(campaign.end_date_time));
      setSelectedAccountId(campaign.google_ads_account_id ?? null);
    }
  }, [campaign]);

  useEffect(() => {
    setAccountsLoading(true);
    fetch("/api/google_ads/accounts", { headers: { Accept: "application/json" } })
      .then((res) => res.json())
      .then((data) => {
        if (data && Array.isArray(data.accounts)) {
          // Only standard accounts can be used for creating campaigns; manager (MCC) accounts cannot
          const standardAccounts = data.accounts.filter((a) => !a.is_manager);
          setAccounts(standardAccounts);
          // If campaign is linked to a manager account, clear selection so user picks a standard account
          if (campaign?.google_ads_account_id && !standardAccounts.some((a) => a.id === campaign.google_ads_account_id)) {
            setSelectedAccountId(null);
          }
        }
        setAccountsLoading(false);
      })
      .catch(() => setAccountsLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!campaign?.id) return;
    setSaving(true);
    try {
      const payload = {
        name: (name || "").trim() || undefined,
        budget_name: (budgetName || "").trim() || undefined,
        amount_micros: dollarsToMicros(amountDollars),
        google_ads_account_id: selectedAccountId || undefined,
        start_date_time: startDate ? `${startDate}T00:00:00` : null,
        end_date_time: endDate ? `${endDate}T23:59:59` : null,
      };
      const res = await fetch(`/api/google_ads/campaigns/${campaign.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        toast?.success("Campaign saved.");
        onSave?.(data);
        onRefetch?.();
        setStartDate(data.start_date || toDateValue(data.start_date_time));
        setEndDate(data.end_date || toDateValue(data.end_date_time));
      } else {
        toast?.error(data.errors?.join(" ") || "Failed to save campaign");
      }
    } catch (err) {
      toast?.error("Failed to save campaign");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncStatus = async () => {
    if (!campaign?.id || !campaign?.google_campaign_id) {
      toast?.error("Publish campaign to Google Ads to sync status.");
      return;
    }
    setSyncingStatus(true);
    try {
      const res = await fetch(`/api/google_ads/campaigns/${campaign.id}/sync_status`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (res.ok && data?.id) {
        onSave?.(data);
        onRefetch?.();
        toast?.success("Campaign status synced.");
      } else {
        toast?.error(data?.error || "Failed to sync status.");
      }
    } catch (e) {
      toast?.error("Failed to sync status.");
      console.error(e);
    } finally {
      setSyncingStatus(false);
    }
  };

  if (!campaign) return null;

  const campaignStatus = (campaign.status || "").toLowerCase();
  const isCampaignDraft = campaignStatus === "draft";
  const hasGoogleCampaignId = Boolean(campaign.google_campaign_id);
  const displayValue = (v) => (v != null && v !== "" ? String(v) : "—");
  const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 4, fontSize: 14, boxSizing: "border-box" };
  const reasonsList = Array.isArray(campaign.primary_status_reasons) ? campaign.primary_status_reasons : [];

  return (
    <form onSubmit={handleSubmit} className="ga-overview-panel">
      {hasGoogleCampaignId && (
        <GoogleStatusBlock
          title="Google status"
          rows={[
            { label: "Status", value: campaign.status },
            { label: "Primary status", value: campaign.primary_status },
            { label: "Serving status", value: campaign.serving_status },
          ]}
          reasons={reasonsList}
          onSync={handleSyncStatus}
          syncing={syncingStatus}
          showSyncButton
        />
      )}
      <div className="ga-overview-panel__section">
        <h3 className="ga-overview-panel__section-title">Campaign Settings</h3>
        <div className="ga-overview-panel__fields">
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <span className="ga-overview-panel__field-label">Ad account</span>
            </div>
            <div className="ga-overview-panel__field-value">
              {accountsLoading ? (
                <span style={{ color: "#6b7280" }}>Loading accounts…</span>
              ) : accounts.length > 0 && isCampaignDraft ? (
                <select
                  value={selectedAccountId ?? ""}
                  onChange={(e) => setSelectedAccountId(e.target.value ? Number(e.target.value) : null)}
                  style={inputStyle}
                >
                  <option value="">Select Ad account</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.name || acc.customer_id} {acc.account_status ? `(${acc.account_status})` : ""}
                    </option>
                  ))}
                </select>
              ) : accounts.length > 0 ? (
                <span style={{ fontWeight: 500 }}>{accounts.find((a) => a.id === selectedAccountId)?.name || accounts.find((a) => a.id === selectedAccountId)?.customer_id || displayValue(selectedAccountId)}</span>
              ) : (
                <span style={{ color: "#6b7280" }}>No ad accounts available</span>
              )}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="Campaign name" />
            </div>
            <div className="ga-overview-panel__field-value">
              {isCampaignDraft ? (
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spring Search Campaign" style={inputStyle} />
              ) : (
                <span style={{ fontWeight: 500 }}>{name || displayValue(campaign.name)}</span>
              )}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <span className="ga-overview-panel__field-label">Budget name</span>
            </div>
            <div className="ga-overview-panel__field-value">
              {isCampaignDraft ? (
                <input type="text" value={budgetName} onChange={(e) => setBudgetName(e.target.value)} placeholder="e.g. Daily Budget" style={inputStyle} />
              ) : (
                <span style={{ fontWeight: 500 }}>{budgetName || displayValue(campaign.budget_name)}</span>
              )}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="Budget amount (USD)" />
            </div>
            <div className="ga-overview-panel__field-value">
              {isCampaignDraft ? (
                <input type="text" inputMode="decimal" value={amountDollars} onChange={(e) => setAmountDollars(e.target.value)} placeholder="e.g. 25.00" style={inputStyle} />
              ) : (
                <span style={{ fontWeight: 500 }}>{amountDollars !== "" ? amountDollars : displayValue(microsToDollars(campaign.amount_micros))}</span>
              )}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="Delivery method" />
            </div>
            <div className="ga-overview-panel__field-value" style={{ fontWeight: 500 }}>
              {displayValue(campaign.delivery_method)}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="Advertising channel type" />
            </div>
            <div className="ga-overview-panel__field-value" style={{ fontWeight: 500 }}>
              {displayValue(campaign.advertising_channel_type)}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="Contains EU political advertising" />
            </div>
            <div className="ga-overview-panel__field-value" style={{ fontWeight: 500 }}>
              {displayValue(campaign.contains_eu_political_advertising)}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="Start date" />
            </div>
            <div className="ga-overview-panel__field-value">
              {isCampaignDraft ? (
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={inputStyle}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{formatDateDisplay(campaign.start_date_time)}</span>
              )}
            </div>
          </div>
          <div className="ga-overview-panel__field">
            <div className="ga-overview-panel__field-header">
              <FieldLabel label="End date" />
            </div>
            <div className="ga-overview-panel__field-value">
              {isCampaignDraft ? (
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={inputStyle}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{formatDateDisplay(campaign.end_date_time)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
      {isCampaignDraft && (
        <div className="ga-overview-panel__actions">
          <button type="submit" className="btn ga-btn-save" disabled={saving} style={{ width: "100%" }}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
    </form>
  );
};

export default GoogleCampaignOverviewPanel;
